/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Fragment, ReactNode, useCallback, useMemo } from 'react';
import { GroupTitle } from './GroupTitle';
import { QuestionList } from './QuestionList';
import { QuestionListEmpty } from './QuestionListEmpty';
import { SurveyQuestionListProps } from '../survey-question-list';
import { BulkActionToolbarSelectedUtrv } from './BulkActionToolbar';
import { getQuestionGroupId, getTotalQuestionCount, HighlightQuestion } from '../utils';
import { GroupSelectbox } from './GroupSelectBox';
import { useScrollToElement } from '../hooks/useScrollToElement';
import { QuestionGroupColumn, QuestionListColumn } from '@g17eco/types/survey-question-list';
import { QuestionList as QuestionListType, ScopeQuestionGroupOptionalValue, ScopeQuestionOptionalValue } from '@g17eco/types/survey';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { VerticalDivider } from '@g17eco/atoms/divider';

interface QuestionListGroupsProps
  extends Pick<
    SurveyQuestionListProps,
    'surveyGroups' | 'columns' | 'alternativeCode' | 'surveyId' | 'initiativeId' | 'unitConfig' | 'expandedRowId' | 'submittedUtrvId'
  > {
  toggleSelectQuestion: (utrv: BulkActionToolbarSelectedUtrv) => void;
  columns?: QuestionListColumn[];
  groupColumns?: QuestionGroupColumn[];
  isQuestionSelected: (utrvId: string) => boolean;
  toggleQuestionGroup: (questionGroupId: string) => void;
  enableBulkActions: boolean;
  highlightQuestion?: HighlightQuestion;
  questionGroupDisplay: string[];
  isBookmarked: (utrvId: string) => boolean,
  toggleBookmark: (utrvId: string) => void,
  selectedQuestions: BulkActionToolbarSelectedUtrv[];
  toggleSelectQuestions: (utrvs: BulkActionToolbarSelectedUtrv[]) => void;
  expandedQuestionGroupId?: string;
  resetExpandedQuestionGroup?: () => void;
  renderExpansionRow?: (question: ScopeQuestionOptionalValue, rowIndex: number) => ReactNode;
  getDisabledRowTooltip?: (question: ScopeQuestionOptionalValue) => string | undefined;
}

interface SubgroupTitleProps extends Pick<QuestionListGroupsProps, 'enableBulkActions' | 'toggleSelectQuestions'> {
  subGroup: QuestionListType<ScopeQuestionOptionalValue>;
  questionList: ScopeQuestionOptionalValue[];
  selectedQuestionIds: string[];
  prefix?: ReactNode;
  className?: string;
}

const GroupLabel = ({ label }: { label: string }) => (
  <SimpleTooltip text={label}>
    <div className='text-truncate' style={{ maxWidth: '400px' }}>
      {label}
    </div>
  </SimpleTooltip>
);

const SubgroupTitle = (props: SubgroupTitleProps) => {
  const { subGroup, enableBulkActions, questionList, selectedQuestionIds, toggleSelectQuestions, prefix, className } =
    props;
  return (
    <div className={`ml-2 sub__group__title d-flex align-items-center justify-content-between ${className}`}>
      <div className='d-flex align-items-center gap-2'>
        {prefix}
        {prefix ? <VerticalDivider /> : null}
        <GroupLabel label={subGroup.name} />
      </div>
      {enableBulkActions ? (
        <GroupSelectbox
          questionGroup={subGroup}
          questionList={questionList}
          selectedQuestionIds={selectedQuestionIds}
          toggleSelectQuestions={toggleSelectQuestions}
        />
      ) : null}
    </div>
  );
};

interface QuestionListSubGroupProps extends QuestionListGroupsProps {
  subGroup: QuestionListType<ScopeQuestionOptionalValue>;
  selectedQuestionIds: string[];
  questionGroup: ScopeQuestionGroupOptionalValue;
  questionGroupId: string;
  rowRef?: React.ForwardedRef<HTMLDivElement>;
  startIndex: number;
  groupTitlePrefix?: ReactNode;
  className?: string;
}

const QuestionListSubgroup = (props: QuestionListSubGroupProps) => {
  const {
    questionGroup,
    subGroup,
    enableBulkActions,
    selectedQuestionIds,
    toggleSelectQuestions,
    alternativeCode,
    groupTitlePrefix,
    className,
  } = props;
  const questionList = subGroup.list;
  if (!questionList || questionList.length === 0) {
    return null;
  }

  return (
    <>
      {subGroup.code === '' ? null : (
        <SubgroupTitle
          subGroup={subGroup}
          prefix={groupTitlePrefix}
          className={className}
          questionList={questionList}
          enableBulkActions={enableBulkActions}
          selectedQuestionIds={selectedQuestionIds}
          toggleSelectQuestions={toggleSelectQuestions}
        />
      )}
      <QuestionList
        {...props}
        questionList={questionList}
        alternativeCode={alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode}
        preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
        colour={questionGroup.groupData && questionGroup.groupData.colour}
      />
    </>
  );
};

export const QuestionListGroups = (props: QuestionListGroupsProps) => {
  const {
    surveyGroups,
    toggleQuestionGroup,
    alternativeCode,
    questionGroupDisplay,
    highlightQuestion,
    enableBulkActions,
    selectedQuestions,
    toggleSelectQuestions,
    expandedQuestionGroupId,
    resetExpandedQuestionGroup,
    getDisabledRowTooltip,
  } = props;

  const singleGroup = useMemo(() => surveyGroups.length === 1, [surveyGroups]);
  const questionCount = useMemo(() => surveyGroups.reduce((acc, questionGroup) => acc + questionGroup.list.length, 0), [surveyGroups]);
  const { elementRef: questionGroupRef } = useScrollToElement(resetExpandedQuestionGroup);

  const isGroupOpen = useCallback((questionGroupId: string) => {
    return singleGroup || questionGroupDisplay.includes(questionGroupId);
  }, [singleGroup, questionGroupDisplay]);

  if (questionCount === 0) {
    return <QuestionListEmpty />;
  }

  const selectedQuestionIds = selectedQuestions.map(q => q._id);
  let startIndex = 0;
  return <>
    {surveyGroups.map((questionGroup, i) => {
      const questionList = questionGroup.list;
      const questionCount = getTotalQuestionCount(questionGroup);
      const isRenderedTitle = questionCount > 0;
      const questionGroupId = getQuestionGroupId(i);
      const isOpen = isGroupOpen(questionGroupId);
      const highlightRowRef = highlightQuestion?.groupId === questionGroupId ? highlightQuestion.rowRef : undefined;
      startIndex = startIndex + questionCount;
      let subGroupCountAccumulator = 0;
      const groupRef = questionGroupId === expandedQuestionGroupId ? questionGroupRef : undefined;
      const lockedUtrvIds = questionList.reduce<string[]>((acc, question) => {
        if (getDisabledRowTooltip?.(question) && question.utrv) {
          acc.push(question.utrv._id);
        }
        return acc;
      }, []);
      const toggleEditableQuestions = (utrvs: BulkActionToolbarSelectedUtrv[]) => {
        toggleSelectQuestions(utrvs.filter(utrv => !lockedUtrvIds.includes(utrv._id)));
      }

      return (
        <Fragment key={`group-${i}-${questionGroup.groupName}`}>
          {isRenderedTitle ? (
            <GroupTitle
              columns={props.groupColumns}
              rowRef={groupRef || highlightRowRef}
              isOpen={isOpen}
              toggle={singleGroup ? undefined : () => toggleQuestionGroup(questionGroupId)}
              questionGroup={questionGroup}
              enableBulkActions={enableBulkActions}
              selectedQuestionIds={selectedQuestionIds}
              toggleSelectQuestions={toggleEditableQuestions}
            />
          ) : null}
          {isOpen ? (
            questionGroup.subGroups ? (
              questionGroup.subGroups.map((subGroup, ix) => {
                // return 1st level of subgroups
                if (!subGroup.subGroups) {
                  // calculate sub group index since it counts from zero
                  const subGroupStartIndex = startIndex - questionCount + subGroupCountAccumulator;
                  subGroupCountAccumulator += subGroup.list.length;

                  return (
                    <QuestionListSubgroup
                      {...props}
                      key={`subgroup-${ix}-${subGroup.code}`}
                      questionGroupId={questionGroupId}
                      questionGroup={questionGroup}
                      subGroup={subGroup}
                      enableBulkActions={enableBulkActions}
                      selectedQuestionIds={selectedQuestionIds}
                      rowRef={highlightRowRef}
                      startIndex={subGroupStartIndex}
                    />
                  );
                }
                if (subGroup.list.length === 0) {
                  return null;
                }
                // return 2nd level of subgroups
                let isFirstNonEmpty = true;
                return (
                  <Fragment key={`subgroup-1-${ix}-${subGroup.code}`}>
                    <SubgroupTitle
                      subGroup={subGroup}
                      enableBulkActions={enableBulkActions}
                      questionList={subGroup.list}
                      selectedQuestionIds={selectedQuestionIds}
                      toggleSelectQuestions={toggleEditableQuestions}
                    />
                    {subGroup.subGroups.map((leafGroup, ix) => {
                      if (!leafGroup.subGroups) {
                        // calculate leaf group index since it counts from zero
                        const subGroupStartIndex = startIndex - questionCount + subGroupCountAccumulator;
                        subGroupCountAccumulator += leafGroup.list.length;
                        const className = `${isFirstNonEmpty && leafGroup.list.length > 0 ? 'sub__group__title--compact' : ''} ml-3 fw-normal`;
                        if (isFirstNonEmpty && leafGroup.list.length > 0) {
                          isFirstNonEmpty = false;
                        }

                        return (
                          <QuestionListSubgroup
                            {...props}
                            key={`subgroup-2-${ix}-${leafGroup.code}`}
                            questionGroupId={questionGroupId}
                            questionGroup={questionGroup}
                            subGroup={leafGroup}
                            enableBulkActions={enableBulkActions}
                            selectedQuestionIds={selectedQuestionIds}
                            rowRef={highlightRowRef}
                            startIndex={subGroupStartIndex}
                            className={className}
                          />
                        );
                      }

                      // return 3rd level of subgroups
                      return leafGroup.subGroups.map((leafSubGroup, ix) => {
                        const subGroupStartIndex = startIndex - questionCount + subGroupCountAccumulator;
                        subGroupCountAccumulator += leafSubGroup.list.length;
                        const className = `${isFirstNonEmpty && leafSubGroup.list.length > 0 ? 'sub__group__title--compact' : ''} ml-3 fw-normal`;
                        if (isFirstNonEmpty && leafGroup.list.length > 0) {
                          isFirstNonEmpty = false;
                        }

                        return (
                          <QuestionListSubgroup
                            {...props}
                            key={`subgroup-3-${ix}-${leafSubGroup.code}`}
                            questionGroupId={questionGroupId}
                            questionGroup={questionGroup}
                            subGroup={leafSubGroup}
                            enableBulkActions={enableBulkActions}
                            selectedQuestionIds={selectedQuestionIds}
                            rowRef={highlightRowRef}
                            startIndex={subGroupStartIndex}
                            className={className}
                            groupTitlePrefix={<GroupLabel label={leafGroup.name} />}
                          />
                        );
                      });
                    })}
                  </Fragment>
                );
              })
            ) : (
              <QuestionList
                {...props}
                questionGroupId={questionGroupId}
                questionList={questionList}
                startIndex={startIndex - questionCount}
                alternativeCode={alternativeCode ?? questionGroup.groupData?.alternativeCode ?? questionGroup.groupCode}
                preferredAltCodes={questionGroup.groupData?.preferredAltCodes}
                colour={questionGroup.groupData && questionGroup.groupData.colour}
                rowRef={highlightRowRef}
              />
            )
          ) : null}
        </Fragment>
      );
    })}
  </>;
}
